import type { MapConfig, Configuration, APIResponse } from '../types';
import type { LayerColors } from '../types/canvas';

export interface ColorPreset {
  id: string;
  name: string;
  description?: string;
  colors: LayerColors;
  is_public: boolean;
  created_by?: string;
  is_default: boolean;
  sort_order: number;
  tags?: string[];
  created_at: string;
  updated_at: string;
}

const API_URL = import.meta.env.VITE_API_URL;

/** Pro lokální vývoj: fallback na Bangladesh worker na 8787, pokud VITE_API_URL chybí */
const RESOLVED_API_URL = API_URL || (import.meta.env.DEV ? 'http://127.0.0.1:8787' : '');

// Validace API_URL
if (!API_URL && import.meta.env.PROD) {
  console.warn(
    '⚠️ VITE_API_URL není nastaveno! ' +
    'Barvy a ikony se budou načítat přímo ze Supabase. ' +
    'Pro produkci nastav VITE_API_URL v Cloudflare Pages environment variables.'
  );
}

if (import.meta.env.DEV && !API_URL) {
  console.info(
    '[API] VITE_API_URL není nastaveno – použit fallback http://127.0.0.1:8787. ' +
    'Pro jiný worker nastav VITE_API_URL v .env.local'
  );
}

/**
 * Helper funkce pro vytvoření URL s validací
 */
export function createApiUrl(path: string): string {
  if (!RESOLVED_API_URL) {
    throw new Error(
      'VITE_API_URL není nastaveno! ' +
      'Nastav environment variable VITE_API_URL (lokální dev: http://127.0.0.1:8787). ' +
      'Viz: https://developers.cloudflare.com/pages/platform/build-configuration/#environment-variables'
    );
  }

  try {
    const url = new URL(path, RESOLVED_API_URL);
    return url.toString();
  } catch (error) {
    throw new Error(
      `Neplatná VITE_API_URL: "${RESOLVED_API_URL}". ` +
      'URL musí být platná HTTP/HTTPS adresa (např. https://maps-api-production.xxx.workers.dev)'
    );
  }
}

/**
 * Helper funkce pro fetch s timeoutem
 */
export async function fetchWithTimeout(resource: RequestInfo, options: RequestInit & { timeout?: number } = {}) {
  const { timeout = 15000, ...rest } = options;

  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(resource, {
      ...rest,
      signal: controller.signal
    });
    clearTimeout(id);
    return response;
  } catch (error) {
    clearTimeout(id);
    // Pokud byl abort způsoben timeoutem, vyhodit specifickou chybu
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error(`Request timed out after ${timeout}ms`);
    }
    throw error;
  }
}

export async function createConfiguration(
  config: MapConfig
): Promise<APIResponse<{ configurationId: string; trackingId: string; previewUrl: string }>> {
  try {
    const response = await fetchWithTimeout(createApiUrl('/api/configurations'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(config),
      timeout: 20000, // Delší timeout pro vytvoření konfigurace (zápis do DB)
    });

    const responseData = await response.json();

    if (!response.ok) {
      console.error('API Error Response:', responseData);
      const errorMessage = responseData.error || `HTTP error! status: ${response.status}`;
      const errorDetails = responseData.details ? JSON.stringify(responseData.details) : '';
      return {
        success: false,
        error: errorDetails ? `${errorMessage}\n\nDetails: ${errorDetails}` : errorMessage,
      };
    }

    return { success: true, data: responseData };
  } catch (error) {
    console.error('API Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

export async function getConfiguration(
  id: string
): Promise<APIResponse<Configuration>> {
  try {
    const response = await fetchWithTimeout(createApiUrl(`/api/configurations/${id}`));

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();
    // Worker vrací { success: true, configuration: {...} }
    // Vrátíme přímo configuration jako data
    const configuration = responseData.configuration || responseData;
    return { success: true, data: configuration };
  } catch (error) {
    console.error('API Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

export async function testPayment(
  configId: string
): Promise<APIResponse<{ jobId: string; orderId: string; message: string }>> {
  try {
    const response = await fetchWithTimeout(createApiUrl('/api/test-payment'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ configId }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error('Test payment API Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

// Color Presets API

export async function getColorPresets(
  includePrivate: boolean = false
): Promise<APIResponse<ColorPreset[]>> {
  try {
    const url = new URL(createApiUrl('/api/color-presets'));
    if (includePrivate) {
      url.searchParams.set('include_private', 'true');
    }

    // console.log('[API] Fetching color presets from:', url.toString());
    const response = await fetchWithTimeout(url.toString(), { timeout: 25000 });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[API] HTTP error response:', response.status, errorText);
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }

    const data = await response.json();
    // console.log('[API] Raw response data:', data);

    // Worker vrací { success: true, presets: [...] }
    const presets = data.presets || (Array.isArray(data) ? data : []);
    // console.log('[API] Parsed presets:', presets.length, presets);

    return { success: true, data: presets };
  } catch (error) {
    console.error('Get color presets API Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

export async function getColorPreset(
  id: string
): Promise<APIResponse<ColorPreset>> {
  try {
    const response = await fetchWithTimeout(createApiUrl(`/api/color-presets/${id}`), { timeout: 25000 });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return { success: true, data: data.preset };
  } catch (error) {
    console.error('Get color preset API Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

export async function createColorPreset(
  preset: {
    name: string;
    description?: string;
    colors: LayerColors;
    is_public?: boolean;
    created_by?: string;
    is_default?: boolean;
    sort_order?: number;
    tags?: string[];
  }
): Promise<APIResponse<ColorPreset>> {
  try {
    const response = await fetchWithTimeout(createApiUrl('/api/color-presets'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(preset),
    });

    const responseData = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: responseData.error || `HTTP error! status: ${response.status}`,
      };
    }

    return { success: true, data: responseData.preset };
  } catch (error) {
    console.error('Create color preset API Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

export async function updateColorPreset(
  id: string,
  preset: {
    name?: string;
    description?: string;
    colors?: LayerColors;
    is_public?: boolean;
    is_default?: boolean;
    sort_order?: number;
    tags?: string[];
  }
): Promise<APIResponse<ColorPreset>> {
  try {
    const response = await fetchWithTimeout(createApiUrl(`/api/color-presets/${id}`), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(preset),
    });

    const responseData = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: responseData.error || `HTTP error! status: ${response.status}`,
      };
    }

    return { success: true, data: responseData.preset };
  } catch (error) {
    console.error('Update color preset API Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

export async function deleteColorPreset(
  id: string
): Promise<APIResponse<{ message: string }>> {
  try {
    const response = await fetchWithTimeout(createApiUrl(`/api/color-presets/${id}`), {
      method: 'DELETE',
    });

    const responseData = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: responseData.error || `HTTP error! status: ${response.status}`,
      };
    }

    return { success: true, data: responseData };
  } catch (error) {
    console.error('Delete color preset API Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Získá color preset podle colorScheme (hledá podle tagu)
 * Fallback na lokální default barvy pokud API selže
 */
export async function getDefaultColorPreset(): Promise<APIResponse<ColorPreset>> {
  try {
    const url = new URL(createApiUrl('/api/color-presets'));
    url.searchParams.set('default', 'true');

    const response = await fetchWithTimeout(url.toString(), { timeout: 25000 });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    // Worker vrací { success: true, presets: [...] }
    const presets = data.presets || (Array.isArray(data) ? data : []);

    // Najít preset s is_default = true
    const defaultPreset = presets.find((p: ColorPreset) => p.is_default === true);

    if (defaultPreset) {
      return { success: true, data: defaultPreset };
    }

    return {
      success: false,
      error: 'No default preset found',
    };
  } catch (error) {
    console.error('Get default color preset API Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

export async function getColorPresetByScheme(
  colorScheme: string
): Promise<APIResponse<ColorPreset>> {
  try {
    const url = new URL(createApiUrl('/api/color-presets'));
    url.searchParams.set('tag', colorScheme);
    url.searchParams.set('public', 'true');

    const response = await fetchWithTimeout(url.toString(), { timeout: 25000 });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    // Worker vrací { success: true, presets: [...] }
    const presets = data.presets || data || [];

    // Najít preset s tagem odpovídajícím colorScheme
    const preset = presets.find((p: ColorPreset) =>
      p.tags?.includes(colorScheme) || p.name.toLowerCase() === colorScheme.toLowerCase()
    );

    if (preset) {
      return { success: true, data: preset };
    }

    return {
      success: false,
      error: `No preset found for colorScheme: ${colorScheme}`,
    };
  } catch (error) {
    console.error('Get color preset by scheme API Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

// Map Icons API

export interface MapIcon {
  id: string;
  name: string;
  description?: string;
  category?: string;
  storage_path: string;
  storage_url: string;
  default_width: number;
  default_height: number;
  default_color: string;
  is_active: boolean;
  sort_order: number;
  tags?: string[];
  created_at: string;
  updated_at: string;
}

export async function getMapIcons(
  category?: string,
  includeInactive: boolean = false
): Promise<APIResponse<MapIcon[]>> {
  try {
    const url = new URL(createApiUrl('/api/map-icons'));
    if (category) {
      url.searchParams.set('category', category);
    }
    if (includeInactive) {
      url.searchParams.set('include_inactive', 'true');
    }

    const response = await fetchWithTimeout(url.toString());

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    // Worker vrací { success: true, icons: [...] }
    const icons = data.icons || (Array.isArray(data) ? data : []);

    return { success: true, data: icons };
  } catch (error) {
    console.error('Get map icons API Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

export async function getMapIcon(
  id: string
): Promise<APIResponse<MapIcon>> {
  try {
    const response = await fetchWithTimeout(createApiUrl(`/api/map-icons/${id}`));

    if (!response.ok) {
      if (response.status === 404) {
        return {
          success: false,
          error: 'Icon not found',
        };
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    // Worker vrací { success: true, icon: {...} }
    const icon = data.icon || data;

    return { success: true, data: icon };
  } catch (error) {
    console.error('Get map icon API Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}





